<script lang="ts">
  import { onDestroy, onMount, tick } from 'svelte';
  import { push, replace } from 'svelte-spa-router';
  import { UserService, type CharityOrganizationUpdateData, type CharityUpdateResponse } from '../../lib/api/user.service';
  import { CategoryService, type Category } from '../../lib/api/category.service';
  import { userStore } from '../../stores/auth.store';
  import { AuthService, type User } from '../../lib/api/auth.service';
  import { location } from 'svelte-spa-router';
  import Button from '../../components/Button.svelte';
  import Input from '../../components/Input.svelte';
  import toast from 'svelte-french-toast';
  import { CHARITY_DGR_TAX_OPTIONS, formatCharityDgrTaxStatus } from '../../lib/charity/dgr-tax-status';

  let loading = false;
  let bankLoading = false;
  let initializing = true;
  let fieldErrors: Record<string, string[]> = {};
  let user: User | null = null;
  let charityData: any = null;
  let logoFile: File | null = null;
  let logoPreview = '';
  let logoInput: HTMLInputElement;
  let orgEditMode = false;
  let detailsEditMode = false;
  let logoLoading = false;
  let detailsLoading = false;
  let logoDragOver = false;
  let showLogoViewer = false;

  type SaveFeedback = { kind: 'success' | 'error'; text: string };
  let logoSaveFeedback: SaveFeedback | null = null;

  type ProfileTab = 'organization' | 'bank' | 'email';
  let orgSaveFeedback: SaveFeedback | null = null;
  let detailsSaveFeedback: SaveFeedback | null = null;
  let bankSaveFeedback: SaveFeedback | null = null;

  const toastSuccessOpts = { duration: 5000 };
  const toastErrorOpts = { duration: 7000 };

  const PROFILE_COMPLETION_TOTAL = 4;

  $: locationPath = $location.split('?')[0];
  $: locationQuery = $location.includes('?') ? $location.split('?')[1] : '';
  $: isBankAccountPage = locationPath.startsWith('/charity-bank-account');
  $: isSignInEmailPage = locationPath === '/charity-profile/sign-in-email';
  $: activeTab = ((): ProfileTab => {
    if (isBankAccountPage) return 'bank';
    if (isSignInEmailPage) return 'email';
    return 'organization';
  })();

  function navigateProfileTab(tab: ProfileTab) {
    fieldErrors = {};
    orgEditMode = false;
    detailsEditMode = false;
    orgSaveFeedback = null;
    detailsSaveFeedback = null;
    bankSaveFeedback = null;

    if (tab === 'bank') {
      push('/charity-bank-account');
      return;
    }
    if (tab === 'email') {
      push('/charity-profile/sign-in-email');
      return;
    }
    push('/charity-profile');
  }

  function setActiveTab(tab: Exclude<ProfileTab, 'bank'>) {
    navigateProfileTab(tab);
  }

  function dismissOrgSaveFeedback() {
    orgSaveFeedback = null;
  }

  function dismissBankSaveFeedback() {
    bankSaveFeedback = null;
  }

  function dismissLogoSaveFeedback() {
    logoSaveFeedback = null;
  }

  function startOrgEdit() {
    orgEditMode = true;
    detailsEditMode = false;
    orgSaveFeedback = null;
    detailsSaveFeedback = null;
    fieldErrors = {};
  }

  function handleProfileSetupCta() {
    startOrgEdit();
  }

  function cancelOrgEdit() {
    orgEditMode = false;
    orgSaveFeedback = null;
    fieldErrors = {};
    initializeCharityData();
  }

  function startDetailsEdit() {
    detailsEditMode = true;
    orgEditMode = false;
    detailsSaveFeedback = null;
    orgSaveFeedback = null;
    fieldErrors = {};
  }

  function cancelDetailsEdit() {
    detailsEditMode = false;
    detailsSaveFeedback = null;
    fieldErrors = {};
    initializeCharityData();
  }

  function dismissDetailsSaveFeedback() {
    detailsSaveFeedback = null;
  }

  function httpErrorMessage(error: unknown, fallback: string): string {
    const e = error as { response?: { data?: { message?: string } } };
    return e?.response?.data?.message || fallback;
  }

  function isFieldEmpty(value: string | null | undefined): boolean {
    return !(value || '').trim();
  }

  function formatWebsiteHref(website: string): string {
    const trimmed = (website || '').trim();
    if (!trimmed) return '';
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }

  function sanitizeBankDigits(value: string, maxLength: number): string {
    return value.replace(/\D/g, '').slice(0, maxLength);
  }

  function parseBsbDigits(value: string): string {
    return sanitizeBankDigits(value, 6);
  }

  function formatBsbDisplay(digits: string): string {
    const d = parseBsbDigits(digits);
    if (d.length <= 3) return d;
    return `${d.slice(0, 3)}-${d.slice(3)}`;
  }

  function cursorToDigitIndex(formatted: string, cursor: number): number {
    let count = 0;
    for (let i = 0; i < Math.min(cursor, formatted.length); i++) {
      if (formatted[i] !== '-') count++;
    }
    return count;
  }

  function digitIndexToCursor(formatted: string, digitIndex: number): number {
    if (digitIndex <= 0) return 0;
    let count = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== '-') {
        count++;
        if (count === digitIndex) {
          return i + 1;
        }
      }
    }
    return formatted.length;
  }

  async function updateBsbInput(input: HTMLInputElement, digits: string, cursorDigitIndex: number) {
    organizationData.verified_bsb_no = parseBsbDigits(digits);
    organizationData = organizationData;
    await tick();
    const formatted = formatBsbDisplay(organizationData.verified_bsb_no);
    input.value = formatted;
    const clampedIndex = Math.max(0, Math.min(cursorDigitIndex, organizationData.verified_bsb_no.length));
    const cursor = digitIndexToCursor(formatted, clampedIndex);
    input.setSelectionRange(cursor, cursor);
  }

  function handleBsbInput(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const oldDigits = organizationData.verified_bsb_no;
    const oldFormatted = formatBsbDisplay(oldDigits);
    const cursor = input.selectionStart ?? 0;
    const digitIndex = cursorToDigitIndex(oldFormatted, cursor);

    const newDigits = parseBsbDigits(input.value);
    const lengthDelta = newDigits.length - oldDigits.length;

    let newDigitIndex = digitIndex;
    if (lengthDelta !== 0) {
      newDigitIndex = digitIndex + lengthDelta;
    }
    newDigitIndex = Math.max(0, Math.min(newDigitIndex, newDigits.length));

    void updateBsbInput(input, newDigits, newDigitIndex);
  }

  function handleBsbKeydown(event: KeyboardEvent) {
    if (event.key !== 'Backspace') return;

    const input = event.currentTarget as HTMLInputElement;
    const cursor = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? cursor;
    if (cursor !== selectionEnd) return;

    const formatted = formatBsbDisplay(organizationData.verified_bsb_no);
    if (cursor === 0) return;

    const charBefore = formatted[cursor - 1];
    if (charBefore !== '-') return;

    event.preventDefault();
    const digits = organizationData.verified_bsb_no;
    const digitIndexBeforeDash = cursorToDigitIndex(formatted, cursor) - 1;
    if (digitIndexBeforeDash < 0) return;

    const newDigits = digits.slice(0, digitIndexBeforeDash) + digits.slice(digitIndexBeforeDash + 1);
    void updateBsbInput(input, newDigits, digitIndexBeforeDash);
  }

  function handleAccountNumberInput(event: Event) {
    organizationData.verified_acc_no = sanitizeBankDigits(
      (event.currentTarget as HTMLInputElement).value,
      12
    );
    organizationData = organizationData;
  }

  // Email change modal state
  let showEmailModal = false;
  let emailLoading = false;
  let emailData = {
    email: ''
  };

  // Categories for charity selection
  let categories: Category[] = [];
  let loadingCategories = false;

  type CharityOrgFormData = Omit<
    CharityOrganizationUpdateData,
    'verified_bsb_no' | 'verified_acc_no' | 'verified_acc_name'
  > & {
    verified_bsb_no: string;
    verified_acc_no: string;
    verified_acc_name: string;
  };

  // Charity organization data
  let organizationData: CharityOrgFormData = {
    name: '',
    abn: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    head_office_location: '',
    charity_government_id: '',
    category_uuid: '',
    dgr_tax_status: '',
    description: '',
    summary: '',
    extended_description: '',
    verified_bsb_no: '',
    verified_acc_no: '',
    verified_acc_name: ''
  };

  $: bankDetailsPresent = !!(
    (organizationData.verified_bsb_no || '').trim() ||
    (organizationData.verified_acc_no || '').trim() ||
    (organizationData.verified_acc_name || '').trim()
  );
  $: bankDetailsVerified = charityData?.bank_details_status === 'verified';
  $: bankStatusLabel = !bankDetailsPresent
    ? 'Not on file'
    : bankDetailsVerified
      ? 'Verified'
      : 'Pending verification';
  $: bsbDisplay = formatBsbDisplay(organizationData.verified_bsb_no);
  $: currentLogoUrl = logoPreview || charityData?.logo || '';
  $: categoryName =
    categories.find((c) => c.uuid === organizationData.category_uuid)?.name ||
    charityData?.category?.name ||
    '';
  $: pageTitle = isBankAccountPage ? 'Bank Account' : 'Charity Profile';
  $: pageSubtitle =
    activeTab === 'bank'
      ? 'Bank account details for donation payouts. Leave2Give verifies these by phone before paying your charity.'
      : activeTab === 'email'
        ? 'Update the email you use to sign in. Your organisation contact details are on the Organisation tab.'
        : orgEditMode
          ? 'Update your registered organisation details and public contact information.'
          : detailsEditMode
            ? 'Add or edit the description employees see when choosing your charity.'
            : 'Your logo, registered organisation details, and public contact information.';
  $: profileFieldsComplete = [
    organizationData.name,
    organizationData.category_uuid,
    organizationData.abn,
    organizationData.contact_email || organizationData.contact_phone,
  ].filter((v) => !!(v || '').trim()).length;
  $: profileCompletionPercent = Math.round((profileFieldsComplete / PROFILE_COMPLETION_TOTAL) * 100);
  $: isProfileEditing = orgEditMode || detailsEditMode;
  $: showProfileSetupPrompt = activeTab === 'organization' && !isProfileEditing && profileCompletionPercent < 100;
  $: missingProfileItems = [
    isFieldEmpty(organizationData.name) ? 'Charity name' : null,
    isFieldEmpty(organizationData.category_uuid) ? 'Category' : null,
    isFieldEmpty(organizationData.abn) ? 'ABN' : null,
    isFieldEmpty(organizationData.contact_email) && isFieldEmpty(organizationData.contact_phone)
      ? 'Contact email or phone'
      : null,
  ].filter((item): item is string => item !== null);
  $: profileSetupCtaLabel = 'Add organisation details';
  $: profileSetupDescription =
    'Add your organisation details so donors and employers can find and trust your charity on Leave2Give.';
  $: charityInitials = (organizationData.name || 'C')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'C';
  $: showProfileHero = activeTab === 'organization' && !isProfileEditing && !orgEditMode && !detailsEditMode;
  $: hasAnyDescription = !(
    isFieldEmpty(organizationData.description) &&
    isFieldEmpty(organizationData.summary) &&
    isFieldEmpty(organizationData.extended_description)
  );

  // Load user data on mount
  onMount(async () => {
    if (new URLSearchParams(locationQuery).get('tab') === 'email' && !isSignInEmailPage) {
      replace('/charity-profile/sign-in-email');
    }

    await Promise.all([
      loadUserData(),
      loadCategories()
    ]);
  });

  onDestroy(() => {
    revokeLogoPreview();
  });

  async function loadCategories() {
    loadingCategories = true;
    try {
      const result = await CategoryService.getCategories();
      if (result.success && result.categories) {
        categories = result.categories;
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    } finally {
      loadingCategories = false;
    }
  }

  async function loadUserData() {
    try {
      initializing = true;
      if (!AuthService.isAuthenticated()) {
        toast.error('Failed to load user data');
        return;
      }
      // Always refetch from API so charity org fields (e.g. bank verification) are not stale vs localStorage
      const result = await AuthService.getCurrentUser();
      if (result.success && result.user) {
        user = result.user;
        userStore.set(result.user as User);
        initializeCharityData();
      } else {
        const localUser = AuthService.getUser();
        if (localUser) {
          user = localUser;
          userStore.set(localUser as User);
          initializeCharityData();
        } else {
          toast.error('Failed to load user data');
        }
      }
    } catch (error: any) {
      console.error('Error loading user:', error);
      toast.error('Failed to load user data');
    } finally {
      initializing = false;
    }
  }

  function initializeCharityData() {
    if (!user) return;
    
    try {
      // Get charity data from user.organization.data
      if (user?.organization?.type === 'charity' && user?.organization?.data) {
        charityData = user.organization.data;
        
        // Initialize organization data
        organizationData = {
          name: charityData?.name || '',
          abn: charityData?.abn || charityData?.charity_government_id || '',
          contact_name: charityData?.contact_name || '',
          contact_email: charityData?.contact_email || '',
          contact_phone: charityData?.contact_phone || '',
          website: charityData?.website || '',
          head_office_location: charityData?.head_office_location || '',
          charity_government_id: charityData?.charity_government_id || '',
          category_uuid: charityData?.category_uuid || '',
          dgr_tax_status: charityData?.dgr_tax_status || '',
          description: charityData?.description || '',
          summary: charityData?.summary || '',
          extended_description: charityData?.extended_description || '',
          verified_bsb_no: sanitizeBankDigits(charityData?.verified_bsb_no || '', 6),
          verified_acc_no: sanitizeBankDigits(charityData?.verified_acc_no || '', 12),
          verified_acc_name: charityData?.verified_acc_name || ''
        };
      } else {
        toast.error('No charity associated with this user');
      }
    } catch (error: any) {
      console.error('Failed to initialize charity data:', error);
      toast.error('Failed to load charity information');
    }
  }

  function getFieldError(fieldName: string): string | undefined {
    return fieldErrors[fieldName]?.[0];
  }

  function revokeLogoPreview() {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
      logoPreview = '';
    }
  }

  function resetSelectedLogo() {
    logoFile = null;
    revokeLogoPreview();
    if (logoInput) {
      logoInput.value = '';
    }
  }

  function openLogoPicker() {
    logoInput?.click();
  }

  function openLogoViewer() {
    if (!currentLogoUrl) return;
    showLogoViewer = true;
  }

  function closeLogoViewer() {
    showLogoViewer = false;
  }

  function changeLogoFromViewer() {
    closeLogoViewer();
    openLogoPicker();
  }

  function handleLogoViewerKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeLogoViewer();
    }
  }

  function handleLogoChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    applyLogoFile(input.files?.[0] || null);
  }

  function handleLogoDragOver(event: DragEvent) {
    event.preventDefault();
    logoDragOver = true;
  }

  function handleLogoDragLeave() {
    logoDragOver = false;
  }

  function handleLogoDrop(event: DragEvent) {
    event.preventDefault();
    logoDragOver = false;
    applyLogoFile(event.dataTransfer?.files?.[0] || null);
  }

  function applyLogoFile(file: File | null) {
    delete fieldErrors.logo;
    fieldErrors = { ...fieldErrors };

    if (!file) {
      resetSelectedLogo();
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxBytes = 2 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      fieldErrors = { ...fieldErrors, logo: ['Please upload a JPG, PNG, or WebP logo.'] };
      resetSelectedLogo();
      return;
    }

    if (file.size > maxBytes) {
      fieldErrors = { ...fieldErrors, logo: ['Logo must be smaller than 2 MB.'] };
      resetSelectedLogo();
      return;
    }

    revokeLogoPreview();
    logoFile = file;
    logoPreview = URL.createObjectURL(file);
  }

  // Validation functions
  function validateEmail(email: string): string | null {
    if (!email) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  }
  
  function validatePhone(phone: string): string | null {
    if (!phone) return null;
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
    if (!phoneRegex.test(phone)) return 'Please enter a valid phone number';
    return null;
  }
  
  function validateABN(abn: string): string | null {
    if (!abn) return 'ABN is required';
    const cleanABN = abn.replace(/\s/g, '');
    if (cleanABN.length !== 11) return 'ABN must be 11 digits';
    if (!/^\d+$/.test(cleanABN)) return 'ABN must contain only numbers';
    return null;
  }
  
  function validateURL(url: string): string | null {
    if (!url) return null;
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  }
  
  function validateRequired(value: string, fieldName: string): string | null {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
  }

  /** Optional: if any bank field is filled, require all three for a complete payout profile */
  function validateBankDetailsOptional(): string | null {
    const bsb = (organizationData.verified_bsb_no || '').trim();
    const acc = (organizationData.verified_acc_no || '').trim();
    const name = (organizationData.verified_acc_name || '').trim();
    const any = bsb || acc || name;
    if (!any) return null;
    if (!bsb || !acc || !name) {
      return 'Please enter BSB, account number, and account name together, or leave all bank fields empty.';
    }
    const bsbNorm = bsb.replace(/\D/g, '');
    const accNorm = acc.replace(/\D/g, '');
    if (bsbNorm.length !== 6) {
      return 'BSB must be exactly 6 digits.';
    }
    if (accNorm.length < 1 || accNorm.length > 12) {
      return 'Account number must be 1 to 12 digits.';
    }
    return null;
  }

  function validateOrganizationForm(): boolean {
    const errors: Record<string, string[]> = {};
    
    // Validate required fields
    const nameError = validateRequired(organizationData.name || '', 'Charity name');
    if (nameError) errors.name = [nameError];
    
    const categoryError = validateRequired(organizationData.category_uuid || '', 'Category');
    if (categoryError) errors.category_uuid = [categoryError];
    
    const abnError = validateABN(organizationData.abn || '');
    if (abnError) errors.abn = [abnError];

    if (!organizationData.dgr_tax_status) {
      errors.dgr_tax_status = ['Charity tax status is required'];
    }
    
    // Optional field validations
    if (organizationData.contact_name && organizationData.contact_name.length > 100) {
      errors.contact_name = ['Contact name cannot exceed 100 characters'];
    }

    if (organizationData.contact_email) {
      const contactEmailError = validateEmail(organizationData.contact_email);
      if (contactEmailError) errors.contact_email = [contactEmailError];
    }
    
    if (organizationData.contact_phone) {
      const contactPhoneError = validatePhone(organizationData.contact_phone);
      if (contactPhoneError) errors.contact_phone = [contactPhoneError];
    }
    
    if (organizationData.website) {
      const websiteError = validateURL(organizationData.website);
      if (websiteError) errors.website = [websiteError];
    }
    
    fieldErrors = errors;
    
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.entries(errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('; ');
      toast.error(`Please fix the following errors: ${errorMessages}`);
      return false;
    }
    
    return true;
  }

  function validateBankForm(): boolean {
    const bankErr = validateBankDetailsOptional();
    fieldErrors = bankErr ? { verified_bsb_no: [bankErr] } : {};
    if (bankErr) {
      bankSaveFeedback = { kind: 'error', text: bankErr };
      toast.error(bankErr, toastErrorOpts);
      return false;
    }
    return true;
  }

  function applyCharityUpdateSuccess(result: CharityUpdateResponse) {
    if (user && user.organization && user.organization.data && result.charity) {
      const updatedUser = {
        ...user,
        organization: {
          ...user.organization,
          data: {
            ...user.organization.data,
            ...result.charity
          }
        }
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      userStore.set(updatedUser as User);
      user = updatedUser as User;
      charityData = result.charity;
      if (result.charity.logo) {
        revokeLogoPreview();
      }
    }
  }

  async function handleLogoSubmit() {
    if (!logoFile) {
      toast.error('Choose a logo file before saving.');
      return;
    }

    logoLoading = true;
    logoSaveFeedback = null;
    delete fieldErrors.logo;
    fieldErrors = { ...fieldErrors };

    try {
      const logoResult = await UserService.updateCharityLogo(logoFile);
      if (logoResult.success) {
        applyCharityUpdateSuccess(logoResult);
        resetSelectedLogo();
        const msg = logoResult.message || 'Your charity logo was saved.';
        logoSaveFeedback = { kind: 'success', text: msg };
        toast.success(msg, toastSuccessOpts);
      } else {
        if (logoResult.error?.response?.data?.errors) {
          fieldErrors = { ...fieldErrors, ...logoResult.error.response.data.errors };
        }
        const errMsg = logoResult.message || 'Could not upload your logo. Please try again.';
        logoSaveFeedback = { kind: 'error', text: errMsg };
        toast.error(errMsg, toastErrorOpts);
      }
    } catch (error: unknown) {
      const apiErrors = (error as { response?: { data?: { errors?: Record<string, string[]> } } })
        ?.response?.data?.errors;
      if (apiErrors) {
        fieldErrors = { ...fieldErrors, ...apiErrors };
      }
      const errMsg = httpErrorMessage(error, 'Could not upload your logo. Please try again.');
      logoSaveFeedback = { kind: 'error', text: errMsg };
    } finally {
      logoLoading = false;
    }
  }

  async function handleOrganizationSubmit() {
    if (!validateOrganizationForm()) {
      return;
    }

    loading = true;
    fieldErrors = {};
    orgSaveFeedback = null;
    const {
      verified_bsb_no,
      verified_acc_no,
      verified_acc_name,
      description,
      summary,
      extended_description,
      ...orgFields
    } = organizationData;
    const orgPayload: CharityOrganizationUpdateData = {
      ...orgFields,
      abn: (organizationData.abn || '').replace(/\s/g, ''),
      charity_government_id: (organizationData.abn || '').replace(/\s/g, ''),
    };
    
    try {
      const result: CharityUpdateResponse = await UserService.updateCharityOrganization(orgPayload);
      
      if (result.success) {
        applyCharityUpdateSuccess(result);
        const msg = result.message || 'Your organisation details were saved.';
        orgEditMode = false;
        orgSaveFeedback = { kind: 'success', text: msg };
        toast.success(msg, toastSuccessOpts);
      } else {
        if (result.error?.response?.data?.data?.errors) {
          const errors = result.error.response.data.data.errors;
          fieldErrors = errors;
        }
        const errMsg = result.message || 'Could not save your organisation details. Please try again.';
        orgSaveFeedback = { kind: 'error', text: errMsg };
        toast.error(errMsg, toastErrorOpts);
      }
    } catch (error: any) {
      console.error('Charity update error:', error);
      
      if (error?.response?.data?.data?.errors) {
        const errors = error.response.data.data.errors;
        fieldErrors = errors;
      }
      const errMsg = httpErrorMessage(error, 'Could not save your organisation details. Please try again.');
      orgSaveFeedback = { kind: 'error', text: errMsg };
      toast.error(errMsg, toastErrorOpts);
    } finally {
      loading = false;
    }
  }

  async function handleDetailsSubmit() {
    detailsLoading = true;
    fieldErrors = {};
    detailsSaveFeedback = null;

    const detailsPayload: CharityOrganizationUpdateData = {
      description: organizationData.description,
      summary: organizationData.summary,
      extended_description: organizationData.extended_description,
    };

    try {
      const result: CharityUpdateResponse = await UserService.updateCharityOrganization(detailsPayload);

      if (result.success) {
        applyCharityUpdateSuccess(result);
        const msg = result.message || 'Your charity details were saved.';
        detailsEditMode = false;
        detailsSaveFeedback = { kind: 'success', text: msg };
        toast.success(msg, toastSuccessOpts);
      } else {
        if (result.error?.response?.data?.data?.errors) {
          fieldErrors = result.error.response.data.data.errors;
        }
        const errMsg = result.message || 'Could not save your charity details. Please try again.';
        detailsSaveFeedback = { kind: 'error', text: errMsg };
        toast.error(errMsg, toastErrorOpts);
      }
    } catch (error: unknown) {
      if ((error as any)?.response?.data?.data?.errors) {
        fieldErrors = (error as any).response.data.data.errors;
      }
      const errMsg = httpErrorMessage(error, 'Could not save your charity details. Please try again.');
      detailsSaveFeedback = { kind: 'error', text: errMsg };
      toast.error(errMsg, toastErrorOpts);
    } finally {
      detailsLoading = false;
    }
  }

  async function handleBankSubmit() {
    if (!validateBankForm()) {
      return;
    }

    bankLoading = true;
    fieldErrors = {};
    bankSaveFeedback = null;
    const bankPayload: CharityOrganizationUpdateData = {
      verified_bsb_no: sanitizeBankDigits(organizationData.verified_bsb_no, 6),
      verified_acc_no: sanitizeBankDigits(organizationData.verified_acc_no, 12),
      verified_acc_name: organizationData.verified_acc_name
    };
    
    try {
      const result: CharityUpdateResponse = await UserService.updateCharityOrganization(bankPayload);
      
      if (result.success) {
        applyCharityUpdateSuccess(result);
        if (result.charity) {
          organizationData.verified_bsb_no = result.charity.verified_bsb_no ?? organizationData.verified_bsb_no;
          organizationData.verified_acc_no = result.charity.verified_acc_no ?? organizationData.verified_acc_no;
          organizationData.verified_acc_name = result.charity.verified_acc_name ?? organizationData.verified_acc_name;
        }
        const msg =
          result.message ||
          'Bank details saved. Leave2Give will verify by phone before payouts are made.';
        bankSaveFeedback = { kind: 'success', text: msg };
        toast.success(msg, toastSuccessOpts);
      } else {
        if (result.error?.response?.data?.data?.errors) {
          fieldErrors = result.error.response.data.data.errors;
        }
        const errMsg = result.message || 'Could not save bank details. Please try again.';
        bankSaveFeedback = { kind: 'error', text: errMsg };
        toast.error(errMsg, toastErrorOpts);
      }
    } catch (error: any) {
      console.error('Bank details update error:', error);
      if (error?.response?.data?.data?.errors) {
        fieldErrors = error.response.data.data.errors;
      }
      const errMsg = httpErrorMessage(error, 'Could not save bank details. Please try again.');
      bankSaveFeedback = { kind: 'error', text: errMsg };
      toast.error(errMsg, toastErrorOpts);
    } finally {
      bankLoading = false;
    }
  }

  function openEmailModal() {
    emailData = {
      email: user?.email || ''
    };
    showEmailModal = true;
  }

  function closeEmailModal() {
    showEmailModal = false;
    emailData = { email: '' };
  }

  async function handleEmailUpdate() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailData.email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    if (!emailRegex.test(emailData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    emailLoading = true;
    try {
      const result = await AuthService.updateEmail({
        email: emailData.email
      });
      
      if (result.success) {
        toast.success('Email sent successfully! Please check your email address for verification link.');
        closeEmailModal();
      } else {
        toast.error((result as any)?.error?.response?.data?.data?.errors?.email || 'Failed to send email. Please try again.');
      }
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join(', ');
        toast.error(errorMessages);
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    } finally {
      emailLoading = false;
    }
  }

  function handleEmailModalKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      closeEmailModal();
    } else if (event.key === 'Enter' && !emailLoading) {
      handleEmailUpdate();
    }
  }
</script>

<div class="profile-page">
  {#if initializing}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading profile...</p>
    </div>
  {:else}
    <div class="profile-page-inner">
    <div class="page-header">
      <h1>{pageTitle}</h1>
      <p>{pageSubtitle}</p>
    </div>

    <div class="tabs-container">
      {#if !isBankAccountPage}
      <div class="profile-tabs" role="tablist" aria-label="Profile sections">
        <button
          type="button"
          class="profile-tab"
          class:active={activeTab === 'organization'}
          on:click={() => navigateProfileTab('organization')}
          role="tab"
          aria-selected={activeTab === 'organization'}
          id="tab-organization"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Organisation
        </button>
        <button
          type="button"
          class="profile-tab"
          class:active={activeTab === 'email'}
          on:click={() => navigateProfileTab('email')}
          role="tab"
          aria-selected={activeTab === 'email'}
          id="tab-email"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          Sign-in email
        </button>
      </div>
      {/if}
    </div>

    <div class="profile-container tab-content-wrapper">
      {#if activeTab === 'organization'}
        {#if !isProfileEditing && showProfileSetupPrompt}
          <div class="profile-setup-banner" role="status">
            <div class="profile-setup-copy">
              <span class="profile-setup-eyebrow">Profile setup</span>
              <h2>Complete your charity profile</h2>
              <p>{profileSetupDescription}</p>
              {#if missingProfileItems.length > 0}
                <ul class="profile-setup-checklist">
                  {#each missingProfileItems as item}
                    <li>{item}</li>
                  {/each}
                </ul>
              {/if}
              <div class="profile-progress-wrap">
                <div class="profile-progress" aria-label="Profile {profileCompletionPercent}% complete">
                  <div class="profile-progress-bar" style="width: {profileCompletionPercent}%"></div>
                </div>
                <span class="profile-progress-label">{profileCompletionPercent}% complete</span>
              </div>
            </div>
            <Button type="button" variant="primary" on:click={handleProfileSetupCta}>
              {profileSetupCtaLabel}
            </Button>
          </div>
        {/if}

        {#if orgEditMode}
        <form on:submit|preventDefault={handleOrganizationSubmit} aria-labelledby="tab-organization">
          {#if orgSaveFeedback}
            <div
              class="inline-save-feedback"
              class:success={orgSaveFeedback.kind === 'success'}
              class:error={orgSaveFeedback.kind === 'error'}
              role={orgSaveFeedback.kind === 'error' ? 'alert' : 'status'}
              aria-live="polite"
            >
              <span class="inline-save-feedback-text">{orgSaveFeedback.text}</span>
              <button
                type="button"
                class="inline-save-feedback-dismiss"
                on:click={dismissOrgSaveFeedback}
                aria-label="Dismiss message">×</button>
            </div>
          {/if}
          <div class="section">
            <h2>Organisation details</h2>
            
            <div class="form-row">
              <div class="form-group">
                <Input 
                  label="Charity Name"
                  placeholder="Enter charity organization name"
                  bind:value={organizationData.name}
                  required={true}
                  error={getFieldError('name')}
                  disabled={loading}
                />
              </div>
              
              <div class="form-group">
                <label for="category" class="input-label">
                  Category <span class="required">*</span>
                </label>
                <select 
                  id="category" 
                  bind:value={organizationData.category_uuid} 
                  required 
                  disabled={loading || loadingCategories}
                  class:error={getFieldError('category_uuid')}
                >
                  <option value="">Select category...</option>
                  {#each categories as category}
                    <option value={category.uuid}>{category.name}</option>
                  {/each}
                </select>
                {#if getFieldError('category_uuid')}
                  <div class="field-error">{getFieldError('category_uuid')}</div>
                {/if}
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group full-width-field">
                <Input 
                  label="ABN"
                  placeholder="11-digit ABN"
                  bind:value={organizationData.abn}
                  required={true}
                  error={getFieldError('abn')}
                  disabled={loading}
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="dgr_tax_status" class="input-label">
                  Charity tax status <span class="required">*</span>
                </label>
                <select
                  id="dgr_tax_status"
                  bind:value={organizationData.dgr_tax_status}
                  required
                  disabled={loading}
                  class:error={getFieldError('dgr_tax_status')}
                >
                  <option value="">Select tax status...</option>
                  {#each CHARITY_DGR_TAX_OPTIONS as option}
                    <option value={option.value}>{option.label}</option>
                  {/each}
                </select>
                {#if getFieldError('dgr_tax_status')}
                  <div class="field-error">{getFieldError('dgr_tax_status')}</div>
                {/if}
              </div>
            </div>
          </div>

          <div class="section">
            <h2>Contact Information</h2>
            <p class="section-hint">This is the contact information shown to employers and donors. To change the email you use to log in, use the <strong>Sign-in email</strong> tab.</p>
            
            <div class="form-row">
              <div class="form-group">
                <Input
                  label="Contact Name"
                  placeholder="Contact name (optional)"
                  bind:value={organizationData.contact_name}
                  error={getFieldError('contact_name')}
                  disabled={loading}
                />
              </div>

              <div class="form-group">
                <Input 
                  label="Contact Email"
                  type="email"
                  placeholder="Contact email (optional)"
                  bind:value={organizationData.contact_email}
                  error={getFieldError('contact_email')}
                  disabled={loading}
                />
              </div>
              
              <div class="form-group">
                <Input 
                  label="Contact Phone"
                  type="tel"
                  placeholder="Contact phone (optional)"
                  bind:value={organizationData.contact_phone}
                  error={getFieldError('contact_phone')}
                  disabled={loading}
                />
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <Input 
                  label="Website"
                  type="url"
                  placeholder="https://example.com (optional)"
                  bind:value={organizationData.website}
                  error={getFieldError('website')}
                  disabled={loading}
                />
              </div>
              
              <div class="form-group">
                <Input 
                  label="Head Office Location"
                  placeholder="Head office location (optional)"
                  bind:value={organizationData.head_office_location}
                  error={getFieldError('head_office_location')}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          
          <div class="info-section">
            <div class="info-card">
              <h4>ℹ️ Before you save</h4>
              <p>Fields marked with an asterisk (*) are required. Payout bank details are saved separately on the <strong>Bank Account</strong> page.</p>
            </div>
          </div>
          
          <div class="form-actions">
            <Button type="button" variant="secondary" on:click={cancelOrgEdit} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save organisation'}
            </Button>
          </div>
        </form>
        {:else if detailsEditMode}
          <form on:submit|preventDefault={handleDetailsSubmit} aria-labelledby="description-heading">
            {#if detailsSaveFeedback}
              <div
                class="inline-save-feedback"
                class:success={detailsSaveFeedback.kind === 'success'}
                class:error={detailsSaveFeedback.kind === 'error'}
                role={detailsSaveFeedback.kind === 'error' ? 'alert' : 'status'}
                aria-live="polite"
              >
                <span class="inline-save-feedback-text">{detailsSaveFeedback.text}</span>
                <button
                  type="button"
                  class="inline-save-feedback-dismiss"
                  on:click={dismissDetailsSaveFeedback}
                  aria-label="Dismiss message">×</button>
              </div>
            {/if}

            <div class="section">
              <h2 id="description-heading">About your charity</h2>
              <p class="section-hint">All fields are optional. This text helps employees understand your mission when selecting a charity.</p>

              <div class="form-group">
                <label for="description" class="input-label">Description</label>
                <textarea
                  id="description"
                  bind:value={organizationData.description}
                  placeholder="Enter charity description (optional)"
                  rows="4"
                  disabled={detailsLoading}
                  class:error={getFieldError('description')}
                ></textarea>
                {#if getFieldError('description')}
                  <div class="field-error">{getFieldError('description')}</div>
                {/if}
              </div>

              <div class="form-group">
                <label for="summary" class="input-label">Summary</label>
                <textarea
                  id="summary"
                  bind:value={organizationData.summary}
                  placeholder="Enter a brief summary (optional)"
                  rows="3"
                  disabled={detailsLoading}
                  class:error={getFieldError('summary')}
                ></textarea>
                {#if getFieldError('summary')}
                  <div class="field-error">{getFieldError('summary')}</div>
                {/if}
              </div>

              <div class="form-group">
                <label for="extended_description" class="input-label">Extended description</label>
                <textarea
                  id="extended_description"
                  bind:value={organizationData.extended_description}
                  placeholder="Enter extended description (optional)"
                  rows="6"
                  disabled={detailsLoading}
                  class:error={getFieldError('extended_description')}
                ></textarea>
                {#if getFieldError('extended_description')}
                  <div class="field-error">{getFieldError('extended_description')}</div>
                {/if}
              </div>
            </div>

            <div class="form-actions">
              <Button type="button" variant="secondary" on:click={cancelDetailsEdit} disabled={detailsLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={detailsLoading}>
                {detailsLoading ? 'Saving...' : 'Save details'}
              </Button>
            </div>
          </form>
        {:else}
          <div class="org-view-layout">
          {#if showProfileHero}
            <header class="profile-hero" aria-label="Charity overview">
              <form class="profile-hero-main" on:submit|preventDefault={handleLogoSubmit}>
                <div
                  class="profile-hero-logo-wrap"
                  role="region"
                  aria-label="Logo upload drop zone"
                  on:dragover={handleLogoDragOver}
                  on:dragleave={handleLogoDragLeave}
                  on:drop={handleLogoDrop}
                >
                  {#if currentLogoUrl}
                    <button
                      type="button"
                      class="profile-hero-logo has-logo"
                      class:drag-over={logoDragOver}
                      on:click={openLogoViewer}
                      aria-label="View charity logo full size"
                    >
                      <img src={currentLogoUrl} alt="{organizationData.name || 'Charity'} logo" />
                      <span class="profile-hero-logo-overlay">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        View
                      </span>
                    </button>
                    <button
                      type="button"
                      class="profile-hero-logo-change"
                      on:click={openLogoPicker}
                      disabled={logoLoading}
                    >
                      Change logo
                    </button>
                  {:else}
                    <button
                      type="button"
                      class="profile-hero-logo"
                      class:drag-over={logoDragOver}
                      on:click={openLogoPicker}
                      aria-label="Upload charity logo"
                    >
                      <span class="profile-hero-initials" aria-hidden="true">{charityInitials}</span>
                      <span class="profile-hero-logo-overlay">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Upload
                      </span>
                    </button>
                  {/if}
                </div>

                <div class="profile-hero-copy">
                  <span class="profile-hero-eyebrow">Your public profile</span>
                  <h2 class="profile-hero-title">
                    {organizationData.name || 'Your charity'}
                  </h2>
                  <div class="profile-hero-meta">
                    {#if categoryName}
                      <span class="profile-category-pill">{categoryName}</span>
                    {/if}
                    {#if organizationData.abn}
                      <span class="profile-hero-abn">ABN {organizationData.abn}</span>
                    {/if}
                  </div>
                  <p class="profile-hero-hint">
                    This is how employers and donors see your charity when browsing Leave2Give.
                  </p>
                  {#if logoFile}
                    <div class="profile-hero-logo-actions">
                      <span class="logo-selected">Selected: {logoFile.name}</span>
                      <button type="button" class="logo-clear-button" on:click={resetSelectedLogo} disabled={logoLoading}>
                        Remove
                      </button>
                      <Button type="submit" variant="primary" disabled={logoLoading}>
                        {logoLoading ? 'Saving...' : 'Save logo'}
                      </Button>
                    </div>
                  {:else}
                    <p class="logo-help">
                      {#if currentLogoUrl}
                        Click the logo to view it full size, or use Change logo to upload a new image. JPG, PNG, or WebP up to 2 MB.
                      {:else}
                        JPG, PNG, or WebP up to 2 MB. Drag and drop or click the logo area.
                      {/if}
                    </p>
                  {/if}
                  {#if getFieldError('logo')}
                    <div class="field-error">{getFieldError('logo')}</div>
                  {/if}
                </div>

                <div
                  class="profile-hero-status"
                  class:profile-complete={profileCompletionPercent >= 100}
                  aria-label="Profile completion {profileCompletionPercent}%"
                >
                  <div
                    class="profile-completion-ring"
                    style="--completion: {profileCompletionPercent}"
                    role="img"
                    aria-label="{profileCompletionPercent}% profile complete"
                  >
                    <span class="profile-completion-value">{profileCompletionPercent}%</span>
                  </div>
                  <span class="profile-completion-caption">Profile complete</span>
                </div>

                <input
                  bind:this={logoInput}
                  id="charity-logo"
                  class="logo-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  on:change={handleLogoChange}
                  disabled={logoLoading}
                />
              </form>

              {#if logoSaveFeedback}
                <div
                  class="inline-save-feedback hero-feedback"
                  class:success={logoSaveFeedback.kind === 'success'}
                  class:error={logoSaveFeedback.kind === 'error'}
                  role={logoSaveFeedback.kind === 'error' ? 'alert' : 'status'}
                  aria-live="polite"
                >
                  <span class="inline-save-feedback-text">{logoSaveFeedback.text}</span>
                  <button
                    type="button"
                    class="inline-save-feedback-dismiss"
                    on:click={dismissLogoSaveFeedback}
                    aria-label="Dismiss message">×</button>
                </div>
              {/if}
            </header>
          {/if}

          {#if orgSaveFeedback}
            <div
              class="inline-save-feedback"
              class:success={orgSaveFeedback.kind === 'success'}
              class:error={orgSaveFeedback.kind === 'error'}
              role={orgSaveFeedback.kind === 'error' ? 'alert' : 'status'}
              aria-live="polite"
            >
              <span class="inline-save-feedback-text">{orgSaveFeedback.text}</span>
              <button
                type="button"
                class="inline-save-feedback-dismiss"
                on:click={dismissOrgSaveFeedback}
                aria-label="Dismiss message">×</button>
            </div>
          {/if}
          {#if detailsSaveFeedback}
            <div
              class="inline-save-feedback"
              class:success={detailsSaveFeedback.kind === 'success'}
              class:error={detailsSaveFeedback.kind === 'error'}
              role={detailsSaveFeedback.kind === 'error' ? 'alert' : 'status'}
              aria-live="polite"
            >
              <span class="inline-save-feedback-text">{detailsSaveFeedback.text}</span>
              <button
                type="button"
                class="inline-save-feedback-dismiss"
                on:click={dismissDetailsSaveFeedback}
                aria-label="Dismiss message">×</button>
            </div>
          {/if}

          <div class="view-cards">
            <section class="view-card" aria-labelledby="org-info-heading">
              <div class="view-card-header">
                <div class="view-card-title-group">
                  <svg class="view-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  <h2 id="org-info-heading">Organisation</h2>
                </div>
                <button type="button" class="card-edit-btn" on:click={startOrgEdit}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit
                </button>
              </div>
              <div class="info-tile-grid">
                <div class="info-tile">
                  <span class="info-tile-label">Charity name</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(organizationData.name)}>
                    {organizationData.name || 'Not set'}
                  </span>
                </div>
                <div class="info-tile">
                  <span class="info-tile-label">Category</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(categoryName)}>
                    {categoryName || 'Not set'}
                  </span>
                </div>
                <div class="info-tile">
                  <span class="info-tile-label">ABN</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(organizationData.abn)}>
                    {organizationData.abn || 'Not set'}
                  </span>
                </div>
                <div class="info-tile">
                  <span class="info-tile-label">Charity tax status</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(organizationData.dgr_tax_status)}>
                    {organizationData.dgr_tax_status
                      ? formatCharityDgrTaxStatus(organizationData.dgr_tax_status)
                      : 'Not set'}
                  </span>
                </div>
              </div>
            </section>

            <section class="view-card" aria-labelledby="contact-heading">
              <div class="view-card-header">
                <div class="view-card-title-group">
                  <svg class="view-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  <div>
                    <h2 id="contact-heading">Public contact details</h2>
                    <p class="view-card-hint">Shown to employers and donors. Your sign-in email is on the <strong>Sign-in email</strong> tab.</p>
                  </div>
                </div>
              </div>
              <div class="info-tile-grid">
                <div class="info-tile">
                  <span class="info-tile-label">Contact name</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(organizationData.contact_name)}>
                    {organizationData.contact_name || 'Not set'}
                  </span>
                </div>
                <div class="info-tile">
                  <span class="info-tile-label">Contact email</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(organizationData.contact_email)}>
                    {#if organizationData.contact_email}
                      <a href="mailto:{organizationData.contact_email}" class="info-link">{organizationData.contact_email}</a>
                    {:else}
                      Not set
                    {/if}
                  </span>
                </div>
                <div class="info-tile">
                  <span class="info-tile-label">Contact phone</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(organizationData.contact_phone)}>
                    {organizationData.contact_phone || 'Not set'}
                  </span>
                </div>
                <div class="info-tile">
                  <span class="info-tile-label">Website</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(organizationData.website)}>
                    {#if organizationData.website}
                      <a href={formatWebsiteHref(organizationData.website)} class="info-link" target="_blank" rel="noopener noreferrer">
                        {organizationData.website}
                      </a>
                    {:else}
                      Not set
                    {/if}
                  </span>
                </div>
                <div class="info-tile">
                  <span class="info-tile-label">Head office location</span>
                  <span class="info-tile-value" class:is-empty={isFieldEmpty(organizationData.head_office_location)}>
                    {organizationData.head_office_location || 'Not set'}
                  </span>
                </div>
              </div>
            </section>

            <section class="view-card" aria-labelledby="description-heading">
              <div class="view-card-header">
                <div class="view-card-title-group">
                  <svg class="view-card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <div>
                    <h2 id="description-heading">About your charity</h2>
                    <p class="view-card-hint">Optional text shown to employees choosing a charity to support.</p>
                  </div>
                </div>
                <button type="button" class="card-edit-btn" on:click={startDetailsEdit}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  {hasAnyDescription ? 'Edit' : 'Add details'}
                </button>
              </div>

              {#if !hasAnyDescription}
                <div class="empty-details-prompt">
                  <p>No charity description added yet. Help employees understand your mission by adding a short summary.</p>
                  <button type="button" class="card-edit-btn card-edit-btn-prominent" on:click={startDetailsEdit}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add charity details
                  </button>
                </div>
              {:else}
                <div class="info-tile-stack">
                  <div class="info-tile info-tile-wide">
                    <span class="info-tile-label">Description</span>
                    <span class="info-tile-value multiline" class:is-empty={isFieldEmpty(organizationData.description)}>
                      {organizationData.description || 'No description added yet.'}
                    </span>
                  </div>
                  <div class="info-tile info-tile-wide">
                    <span class="info-tile-label">Summary</span>
                    <span class="info-tile-value multiline" class:is-empty={isFieldEmpty(organizationData.summary)}>
                      {organizationData.summary || 'No summary added yet.'}
                    </span>
                  </div>
                  <div class="info-tile info-tile-wide">
                    <span class="info-tile-label">Extended description</span>
                    <span class="info-tile-value multiline" class:is-empty={isFieldEmpty(organizationData.extended_description)}>
                      {organizationData.extended_description || 'No extended description added yet.'}
                    </span>
                  </div>
                </div>
              {/if}
            </section>
          </div>
          </div>
        {/if}
      {:else if activeTab === 'bank'}
        <form on:submit|preventDefault={handleBankSubmit} aria-labelledby="bank-account-heading">
          {#if bankSaveFeedback}
            <div
              class="inline-save-feedback"
              class:success={bankSaveFeedback.kind === 'success'}
              class:error={bankSaveFeedback.kind === 'error'}
              role={bankSaveFeedback.kind === 'error' ? 'alert' : 'status'}
              aria-live="polite"
            >
              <span class="inline-save-feedback-text">{bankSaveFeedback.text}</span>
              <button
                type="button"
                class="inline-save-feedback-dismiss"
                on:click={dismissBankSaveFeedback}
                aria-label="Dismiss message">×</button>
            </div>
          {/if}
          <div class="section bank-section">
            <h2 id="bank-account-heading">Bank details for payouts</h2>
            <p class="bank-disclaimer">
              Leave2Give will phone your charity to verify these details before any payments are made. Please ensure they match your organisation’s bank records.
            </p>
            <div class="bank-status-row">
              <span class="bank-status-label">Bank details status:</span>
              <span
                class="bank-status-badge"
                class:verified={bankDetailsVerified}
                class:pending={bankDetailsPresent && !bankDetailsVerified}
                class:empty={!bankDetailsPresent}
              >
                {bankStatusLabel}
              </span>
            </div>
            <p class="bank-status-hint">
              {#if !bankDetailsPresent}
                No bank details on file yet. You can add them below when you are ready to receive payouts.
              {:else if bankDetailsVerified}
                These details have been confirmed with your charity by phone.
              {:else}
                Your bank details are on file and awaiting phone verification. If you change them, status returns to pending until we confirm again.
              {/if}
            </p>
            <div class="form-row">
              <div class="form-group">
                <Input
                  label="BSB"
                  placeholder="e.g. 062-000"
                  value={bsbDisplay}
                  error={getFieldError('verified_bsb_no')}
                  disabled={bankLoading}
                  inputmode="numeric"
                  on:input={handleBsbInput}
                  on:keydown={handleBsbKeydown}
                />
              </div>
              <div class="form-group">
                <Input
                  label="Account number"
                  placeholder="Up to 12 digits"
                  bind:value={organizationData.verified_acc_no}
                  error={getFieldError('verified_acc_no')}
                  disabled={bankLoading}
                  inputmode="numeric"
                  maxlength={12}
                  on:input={handleAccountNumberInput}
                />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group full-width-field">
                <Input
                  label="Account name"
                  placeholder="Name on the account"
                  bind:value={organizationData.verified_acc_name}
                  error={getFieldError('verified_acc_name')}
                  disabled={bankLoading}
                />
              </div>
            </div>
          </div>

          <div class="info-section">
            <div class="info-card">
              <h4>ℹ️ Payouts</h4>
              <p>Only this page updates BSB and account details. Your charity profile and sign-in email are updated elsewhere.</p>
            </div>
          </div>

          <div class="form-actions">
            <Button type="submit" variant="primary" disabled={bankLoading}>
              {bankLoading ? 'Saving...' : 'Save bank details'}
            </Button>
          </div>
        </form>
      {:else}
        <div class="section email-tab" aria-labelledby="tab-email">
          <h2>Sign-in email</h2>
          <p class="section-description">This is the address you use to log in to Leave2Give. It is separate from your organisation’s public contact email (on the Organization tab). A verification message is sent when you change it.</p>
          
          <div class="email-section">
            <div class="current-email-card">
              <div class="current-email-header">
                <svg class="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <h3>Current sign-in email</h3>
              </div>
              <div class="current-email-value">
                {user?.email || 'Not provided'}
              </div>
            </div>
            
            <div class="email-actions">
              <Button 
                variant="primary" 
                on:click={openEmailModal}
                disabled={emailLoading}
                type="button"
              >
                Change sign-in email
              </Button>
            </div>
          </div>

          <div class="info-section email-tab-info">
            <div class="info-card">
              <h4>ℹ️ Verification</h4>
              <p>After you request a change, check your new inbox for a link to confirm. Your current email stays active until you complete that step.</p>
            </div>
          </div>
        </div>
      {/if}
    </div>
    </div>
  {/if}
</div>

<!-- Logo viewer modal -->
{#if showLogoViewer && currentLogoUrl}
  <div
    class="modal-overlay"
    role="presentation"
    on:click|self={closeLogoViewer}
    on:keydown={(e) => e.key === 'Escape' && closeLogoViewer()}
  >
    <div
      class="modal-content logo-viewer-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logo-viewer-title"
    >
      <div class="modal-header">
        <h3 id="logo-viewer-title">{organizationData.name || 'Charity'} logo</h3>
        <button type="button" class="modal-close" on:click={closeLogoViewer} aria-label="Close">×</button>
      </div>

      <div class="logo-viewer-body">
        <img src={currentLogoUrl} alt="{organizationData.name || 'Charity'} logo full size" />
      </div>

      <div class="modal-footer">
        <Button variant="secondary" on:click={closeLogoViewer} type="button">Close</Button>
        <Button variant="primary" on:click={changeLogoFromViewer} type="button">Change logo</Button>
      </div>
    </div>
  </div>
{/if}

<!-- Email Change Modal -->
{#if showEmailModal}
  <div 
    class="modal-overlay" 
    role="presentation"
    on:click|self={closeEmailModal}
    on:keydown={(e) => e.key === 'Escape' && closeEmailModal()}
  >
    <div 
      class="modal-content" 
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
    >
      <div class="modal-header">
        <h3 id="email-modal-title">Change Email Address</h3>
        <button class="modal-close" on:click={closeEmailModal}>×</button>
      </div>
      
      <div class="modal-body">
        <div class="form-group">
          <Input 
            type="email"
            label="New Email Address"
            bind:value={emailData.email}
            placeholder="Enter your new email address"
            required={true}
            fullWidth={true}
            disabled={emailLoading}
          />
        </div>
        
        <p class="info-text">
          📧 An email will be sent to the specified email address with a verification link to update your email.
        </p>
      </div>
      
      <div class="modal-footer">
        <Button 
          variant="secondary" 
          on:click={closeEmailModal}
          disabled={emailLoading}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          on:click={handleEmailUpdate}
          disabled={emailLoading || !emailData.email.trim()}
        >
          {emailLoading ? 'Sending...' : 'Send Verification Email'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .profile-page {
    min-height: 100vh;
    background:
      radial-gradient(ellipse 80% 50% at 50% -10%, rgba(255, 180, 0, 0.12), transparent 55%),
      #f7fafc;
    padding: 1.5rem 2rem;
  }

  .profile-page-inner {
    max-width: 1000px;
    margin: 0 auto;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 1rem;
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .loading-container p {
    color: #6b7280;
    font-size: 0.875rem;
  }

  .page-header {
    margin-bottom: 1.5rem;
  }

  .page-header h1 {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 0.5rem 0;
    letter-spacing: -0.02em;
  }

  .page-header p {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }

  /* Tabs */
  .tabs-container {
    margin-bottom: 1.5rem;
  }

  .profile-tabs {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    padding: 0.35rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .profile-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.125rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 9999px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
  }

  .profile-tab:hover {
    color: #111827;
    background: #f9fafb;
  }

  .profile-tab.active {
    color: #111827;
    background: var(--color-primary);
    box-shadow: 0 1px 3px rgba(255, 180, 0, 0.35);
  }

  .profile-tab svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .tab-content-wrapper {
    width: 100%;
  }

  /* Email Section */
  .email-section {
    margin-top: 1.5rem;
  }

  .current-email-card {
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .current-email-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .current-email-header h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
  }

  .email-icon {
    color: var(--color-primary);
    flex-shrink: 0;
  }

  .current-email-value {
    font-size: 1.125rem;
    font-weight: 500;
    color: #111827;
    word-break: break-all;
    padding: 0.75rem 1rem;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
  }

  .email-actions {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
  }

  .section-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1rem 0;
    line-height: 1.5;
  }

  .section-hint {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1.25rem 0;
    line-height: 1.5;
  }

  .email-tab .info-section.email-tab-info {
    margin-top: 2rem;
    padding-top: 0;
    border-top: none;
  }

  .profile-container {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    padding: 2rem;
    width: 100%;
  }

  .profile-setup-banner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    padding: 1.35rem 1.5rem;
    border: 1px solid #fcd34d;
    border-radius: 14px;
    background: linear-gradient(135deg, #fffbeb 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(251, 191, 36, 0.12);
  }

  .profile-setup-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #b45309;
  }

  .profile-setup-copy h2 {
    margin: 0 0 0.35rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #92400e;
  }

  .profile-setup-copy p {
    margin: 0 0 0.75rem 0;
    font-size: 0.875rem;
    color: #b45309;
    line-height: 1.5;
    max-width: 36rem;
  }

  .profile-setup-checklist {
    margin: 0 0 0.85rem 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .profile-setup-checklist li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    color: #92400e;
  }

  .profile-setup-checklist li::before {
    content: '';
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    background: #f59e0b;
    flex-shrink: 0;
  }

  .profile-progress-wrap {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .profile-progress {
    width: 100%;
    max-width: 14rem;
    height: 0.45rem;
    border-radius: 9999px;
    background: #fde68a;
    overflow: hidden;
  }

  .profile-progress-bar {
    height: 100%;
    border-radius: 9999px;
    background: var(--color-primary);
    transition: width 0.3s ease;
  }

  .profile-progress-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #92400e;
  }

  .profile-category-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.65rem;
    border-radius: 9999px;
    background: #dbeafe;
    color: #1e40af;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .org-view-layout {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .org-view-layout > .view-cards {
    margin-top: 0;
  }

  .profile-hero {
    margin: -0.5rem 0 0;
    padding: 1.5rem;
    border-radius: 16px;
    border: 1px solid #e5e7eb;
    background:
      linear-gradient(145deg, #ffffff 0%, #fffdf7 55%, #fff9eb 100%);
    box-shadow: 0 8px 24px rgba(17, 24, 39, 0.06);
    animation: profileHeroIn 0.45s ease-out;
  }

  @keyframes profileHeroIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .profile-hero-logo-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .profile-hero-logo-change {
    padding: 0;
    border: none;
    background: none;
    color: #1d4ed8;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .profile-hero-logo-change:hover:not(:disabled) {
    color: #1e40af;
  }

  .profile-hero-logo-change:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .profile-hero-logo-change:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.2);
    border-radius: 4px;
  }

  .logo-viewer-modal {
    max-width: min(720px, 92vw);
  }

  .logo-viewer-body {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 2rem;
    background: #f9fafb;
    max-height: min(70vh, 640px);
    overflow: auto;
  }

  .logo-viewer-body img {
    max-width: 100%;
    max-height: min(65vh, 600px);
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(17, 24, 39, 0.12);
    background: white;
  }

  .profile-hero-main {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 1.5rem;
    align-items: center;
  }

  .profile-hero-logo {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 6.5rem;
    height: 6.5rem;
    padding: 0;
    border: 2px dashed #d1d5db;
    border-radius: 18px;
    background: white;
    cursor: pointer;
    overflow: hidden;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  }

  .profile-hero-logo:hover,
  .profile-hero-logo.drag-over {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 4px rgba(255, 180, 0, 0.15);
    transform: translateY(-1px);
  }

  .profile-hero-logo:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 4px rgba(255, 180, 0, 0.2);
  }

  .profile-hero-logo.has-logo {
    border-style: solid;
    padding: 0.35rem;
  }

  .profile-hero-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
  }

  .profile-hero-initials {
    font-size: 1.5rem;
    font-weight: 700;
    color: #9ca3af;
    letter-spacing: 0.04em;
  }

  .profile-hero-logo-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    background: rgba(17, 24, 39, 0.55);
    color: white;
    font-size: 0.6875rem;
    font-weight: 600;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .profile-hero-logo-overlay svg {
    width: 1.1rem;
    height: 1.1rem;
  }

  .profile-hero-logo:hover .profile-hero-logo-overlay,
  .profile-hero-logo.drag-over .profile-hero-logo-overlay {
    opacity: 1;
  }

  .profile-hero-copy {
    min-width: 0;
  }

  .profile-hero-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  .profile-hero-title {
    margin: 0 0 0.5rem 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.02em;
    line-height: 1.2;
    word-break: break-word;
  }

  .profile-hero-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .profile-hero-abn {
    font-size: 0.8125rem;
    color: #6b7280;
    font-weight: 500;
  }

  .profile-hero-hint {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
    max-width: 32rem;
  }

  .profile-hero-logo-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.75rem;
  }

  .profile-hero-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    text-align: center;
  }

  .profile-completion-ring {
    --size: 5.5rem;
    --track: #e5e7eb;
    --fill: var(--color-primary);
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    display: grid;
    place-items: center;
    background:
      radial-gradient(closest-side, white 72%, transparent 74% 100%),
      conic-gradient(var(--fill) calc(var(--completion) * 1%), var(--track) 0);
  }

  .profile-hero-status.profile-complete .profile-completion-ring {
    --fill: var(--color-positive, #2a9d8f);
  }

  .profile-completion-value {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
  }

  .profile-hero-status.profile-complete .profile-completion-value {
    color: #065f46;
  }

  .profile-completion-caption {
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
  }

  .profile-hero-status.profile-complete .profile-completion-caption {
    color: #065f46;
    font-weight: 600;
  }

  .hero-feedback {
    margin: 1rem 0 0;
  }

  .card-edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 0.85rem;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    background: white;
    color: #374151;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    flex-shrink: 0;
  }

  .card-edit-btn svg {
    width: 0.9rem;
    height: 0.9rem;
  }

  .card-edit-btn:hover {
    background: #fffbeb;
    border-color: #fcd34d;
    color: #92400e;
  }

  .card-edit-btn:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.2);
  }

  .card-edit-btn-prominent {
    padding: 0.55rem 1rem;
    border-color: var(--color-primary);
    background: var(--color-primary);
    color: #111827;
  }

  .card-edit-btn-prominent:hover {
    background: var(--color-primary-hover);
    border-color: var(--color-primary-hover);
    color: #111827;
  }

  .view-card-title-group {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    min-width: 0;
  }

  .view-card-icon {
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.15rem;
    color: var(--color-secondary);
    flex-shrink: 0;
  }

  .org-view-layout .inline-save-feedback {
    margin-bottom: 0;
  }

  .view-cards {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .view-card {
    padding: 1.35rem 1.5rem;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: white;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
    animation: cardIn 0.35s ease-out backwards;
  }

  .view-card:nth-child(1) { animation-delay: 0.05s; }
  .view-card:nth-child(2) { animation-delay: 0.1s; }
  .view-card:nth-child(3) { animation-delay: 0.15s; }

  @keyframes cardIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .view-card:hover {
    border-color: #d1d5db;
    box-shadow: 0 4px 12px rgba(17, 24, 39, 0.05);
  }

  .view-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .view-card h2 {
    margin: 0;
    font-size: 1.0625rem;
    font-weight: 600;
    color: #111827;
  }

  .view-card-hint {
    margin: 0.25rem 0 0 0;
    font-size: 0.8125rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .info-tile-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }

  .info-tile-stack {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .info-tile {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    padding: 0.875rem 1rem;
    border-radius: 10px;
    background: #f9fafb;
    border: 1px solid #f3f4f6;
  }

  .info-tile-wide {
    width: 100%;
  }

  .info-tile-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .info-tile-value {
    font-size: 0.9375rem;
    font-weight: 500;
    color: #111827;
    line-height: 1.45;
    word-break: break-word;
  }

  .info-tile-value.multiline {
    white-space: pre-wrap;
    font-weight: 400;
  }

  .info-tile-value.is-empty {
    color: #9ca3af;
    font-weight: 400;
    font-style: italic;
  }

  .info-link {
    color: #1d4ed8;
    text-decoration: none;
  }

  .info-link:hover {
    text-decoration: underline;
  }

  .empty-details-prompt {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.25rem;
    border-radius: 8px;
    background: white;
    border: 1px dashed #d1d5db;
  }

  .empty-details-prompt p {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .inline-save-feedback {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    padding: 0.875rem 1rem;
    border-radius: 8px;
    border: 1px solid;
    font-size: 0.9rem;
    line-height: 1.45;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .inline-save-feedback.success {
    background: #ecfdf5;
    border-color: #6ee7b7;
    color: #065f46;
  }

  .inline-save-feedback.error {
    background: #fef2f2;
    border-color: #fecaca;
    color: #991b1b;
  }

  .inline-save-feedback-text {
    flex: 1;
    min-width: 0;
  }

  .inline-save-feedback-dismiss {
    flex-shrink: 0;
    width: 1.75rem;
    height: 1.75rem;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: inherit;
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
    opacity: 0.75;
  }

  .inline-save-feedback-dismiss:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.06);
  }

  .section {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .section:last-of-type:not(.logo-section) {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .section h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1.5rem 0;
  }

  .logo-help {
    margin: 0 0 0.75rem 0;
    color: #6b7280;
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .logo-clear-button {
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
  }

  .logo-clear-button:hover {
    background: #f3f4f6;
  }

  .logo-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .logo-selected {
    margin: 0.5rem 0 0 0;
    color: #374151;
    font-size: 0.75rem;
    word-break: break-all;
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .form-row:last-child {
    margin-bottom: 0;
  }

  .full-width-field {
    grid-column: 1 / -1;
  }

  .bank-section .bank-disclaimer {
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0 0 1rem 0;
    padding: 0.75rem 1rem;
    background: #fffbeb;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    color: #92400e;
  }

  .bank-status-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
  }

  .bank-status-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .bank-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .bank-status-badge.verified {
    background: #d1fae5;
    color: #065f46;
  }

  .bank-status-badge.pending {
    background: #fef3c7;
    color: #92400e;
  }

  .bank-status-badge.empty {
    background: #f3f4f6;
    color: #4b5563;
  }

  .bank-status-hint {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1.25rem 0;
    line-height: 1.5;
  }

  .form-group {
    display: flex;
    flex-direction: column;
  }

  .input-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .required {
    color: #dc2626;
  }

  select {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    background-color: white;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }

  select:focus {
    outline: none;
    border-color: var(--color-primary, #f0b429);
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  select.error {
    border-color: #ef4444;
  }

  select:disabled {
    background-color: #f3f4f6;
    color: black;
    cursor: not-allowed;
  }

  textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    font-family: inherit;
    background-color: white;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
    resize: vertical;
  }

  textarea:focus {
    outline: none;
    border-color: var(--color-primary, #f0b429);
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }

  textarea.error {
    border-color: #ef4444;
  }

  textarea:disabled {
    background-color: #f3f4f6;
    color: black;
    cursor: not-allowed;
  }

  .field-error {
    color: #ef4444;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }

  .info-section {
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e5e7eb;
  }

  .info-card {
    background: #fffbeb;
    border: 1px solid #fed7aa;
    border-radius: 8px;
    padding: 1.25rem;
  }

  .info-card h4 {
    color: #d97706;
    font-size: 0.875rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .info-card p {
    color: #92400e;
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e5e7eb;
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalFadeIn 0.3s ease-out;
  }

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
    color: white;
  }

  .modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .modal-close {
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  .modal-close:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  .modal-body {
    padding: 2rem;
  }

  .modal-body .form-group {
    margin-bottom: 1rem;
  }

  .info-text {
    margin-top: 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 1.5rem;
    background-color: #f8fafc;
    border-top: 1px solid #e2e8f0;
  }

  @media (max-width: 768px) {
    .profile-page {
      padding: 1rem;
    }

    .profile-tabs {
      display: flex;
      width: 100%;
      overflow-x: auto;
      border-radius: 12px;
    }

    .profile-tab {
      padding: 0.625rem 0.9rem;
      font-size: 0.813rem;
      white-space: nowrap;
    }

    .profile-tab svg {
      width: 14px;
      height: 14px;
    }

    .profile-hero {
      padding: 1.15rem;
    }

    .profile-hero-main {
      grid-template-columns: 1fr;
      justify-items: center;
      text-align: center;
    }

    .profile-hero-copy {
      text-align: left;
      width: 100%;
    }

    .profile-hero-meta,
    .profile-hero-logo-actions {
      justify-content: flex-start;
    }

    .profile-hero-title {
      font-size: 1.4rem;
    }

    .profile-setup-banner {
      flex-direction: column;
      align-items: stretch;
    }

    .current-email-card {
      padding: 1.25rem;
    }

    .current-email-value {
      font-size: 1rem;
    }

    .profile-container {
      padding: 1.5rem;
    }

    .form-row,
    .info-tile-grid {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }

    .profile-setup-banner,
    .page-header h1 {
      font-size: 1.5rem;
    }
  }
</style>
